import { CAMPAIGN_SCOPE_KIND_LABELS, type CampaignRecord } from "@station/contracts";
import {
  Alert,
  Button,
  DataTable,
  type DataTableColumn,
  Dialog,
  EmptyState,
  FormField,
  Select,
  SkeletonRows,
  StatusBadge,
  useToast,
} from "@station/ui";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Percent, Plus } from "lucide-react";
import { type ReactElement, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { CampaignDialog } from "../../../features/pos/campaign-dialog.js";
import {
  basketThresholdText,
  CAMPAIGN_STATE_TONE,
  campaignState,
  crossConditionText,
  customerKindText,
  effectSummaryText,
  effectText,
  timeWindowText,
  validityText,
  weekdaysText,
} from "../../../features/pos/pos-values.js";
import { useCampaigns, usePosPermissions, useRefreshPos } from "../../../features/pos/queries.js";
import {
  NoStations,
  PosPage,
  PosStationField,
  posSearchShape,
  usePosStations,
} from "../../../features/pos/shared.js";
import { compactSearch } from "../../../features/shifts/shift-values.js";
import { api } from "../../../lib/api.js";
import { useCursorPages } from "../../../lib/cursor-pages.js";
import { errorMessage, isApiError } from "../../../lib/errors.js";

const searchSchema = z.object({
  ...posSearchShape,
  active: z.enum(["true", "false"]).optional().catch(undefined),
});

export const Route = createFileRoute("/_app/kasa/kampanyalar")({
  validateSearch: (search) => searchSchema.parse(search),
  component: CampaignsRoute,
});

/**
 * Kampanya yönetimi (Prompt 15). Kampanya SİLİNMEZ: satış satırları ona bağlıdır (AGENTS.md §3.5),
 * yürürlükten kaldırmak "Kapat" (deactivate) ile yapılır ve kayıt listede kalır.
 */
function CampaignsRoute(): ReactElement {
  const { t } = useTranslation();
  const permissions = usePosPermissions();
  const [editing, setEditing] = useState<{ campaign: CampaignRecord | null } | null>(null);

  return (
    <PosPage
      title={t("menu.posCampaigns")}
      description={t("pos.campaigns.description")}
      breadcrumb={[{ label: t("menu.posCampaigns") }]}
      actions={
        permissions.canManage ? (
          <Button onClick={() => setEditing({ campaign: null })}>
            <Plus aria-hidden="true" />
            {t("pos.campaigns.newTitle")}
          </Button>
        ) : null
      }
    >
      {({ companyId }) => <CampaignsBody companyId={companyId} editing={editing} onEdit={setEditing} />}
    </PosPage>
  );
}

function CampaignsBody({
  companyId,
  editing,
  onEdit,
}: {
  companyId: string;
  editing: { campaign: CampaignRecord | null } | null;
  onEdit: (next: { campaign: CampaignRecord | null } | null) => void;
}): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const station = usePosStations(companyId);
  const permissions = usePosPermissions();
  const [closing, setClosing] = useState<CampaignRecord | null>(null);

  const setFilters = (patch: Record<string, unknown>) =>
    void navigate({
      to: ".",
      search: ((previous: Record<string, unknown>) => compactSearch({ ...previous, ...patch })) as never,
      replace: true,
    });

  const filters = {
    stationId: station.stationId ?? undefined,
    isActive: search.active === undefined ? undefined : search.active === "true",
  };
  const pages = useCursorPages(JSON.stringify(filters));
  const list = useCampaigns(companyId, filters, pages.cursor);

  const columns = useMemo<DataTableColumn<CampaignRecord>[]>(
    () => [
      {
        id: "code",
        header: t("pos.campaigns.fields.code"),
        hideable: false,
        exportValue: (row) => row.code,
        cell: (row) => <span className="font-mono whitespace-nowrap">{row.code}</span>,
      },
      {
        id: "name",
        header: t("pos.campaigns.fields.name"),
        exportValue: (row) => row.name,
        cell: (row) => {
          // Prompt 17: kupon kampanyası ve çapraz koşul rozetleri (anlam METİNLE — renk tek başına değil).
          const cross = crossConditionText(row.crossCondition);
          return (
            <span className="grid gap-1">
              <span>{row.name}</span>
              {row.requiresCoupon || cross ? (
                <span className="flex flex-wrap gap-1">
                  {row.requiresCoupon ? (
                    <StatusBadge tone="info" className="whitespace-nowrap">
                      {t("pos.campaigns.requiresCouponBadge")}
                    </StatusBadge>
                  ) : null}
                  {cross ? (
                    <StatusBadge tone="neutral" className="whitespace-nowrap">
                      {cross}
                    </StatusBadge>
                  ) : null}
                </span>
              ) : null}
            </span>
          );
        },
      },
      {
        id: "couponCross",
        header: t("pos.campaigns.fields.couponCross"),
        defaultVisible: false,
        exportValue: (row) =>
          [
            row.requiresCoupon ? t("pos.campaigns.requiresCouponBadge") : null,
            crossConditionText(row.crossCondition),
          ]
            .filter(Boolean)
            .join(" · ") || null,
        cell: (row) =>
          [
            row.requiresCoupon ? t("pos.campaigns.requiresCouponBadge") : null,
            crossConditionText(row.crossCondition),
          ]
            .filter(Boolean)
            .join(" · ") || <span className="text-muted-foreground">—</span>,
      },
      {
        id: "effect",
        header: t("pos.campaigns.fields.effect"),
        exportValue: (row) => effectSummaryText(row, t("pos.campaigns.basketThreshold")),
        cell: (row) => {
          // Sepet eşiği olan kampanya "sepet kampanyası"dır: satır kampanyalarından SONRA değerlendirilir.
          const threshold = basketThresholdText(row.minBasketGross, t("pos.campaigns.basketThreshold"));
          return (
            <span className="grid gap-0.5">
              <span className="whitespace-nowrap">{effectText(row)}</span>
              {threshold ? <span className="text-xs text-muted-foreground">{threshold}</span> : null}
            </span>
          );
        },
      },
      {
        id: "scope",
        header: t("pos.campaigns.fields.scope"),
        exportValue: (row) => `${CAMPAIGN_SCOPE_KIND_LABELS[row.scopeKind]} (${row.scopeIds.length})`,
        cell: (row) => (
          <span className="grid gap-0.5">
            <span>{CAMPAIGN_SCOPE_KIND_LABELS[row.scopeKind]}</span>
            <span className="text-xs text-muted-foreground">
              {t("pos.campaigns.scopeCount", { count: row.scopeIds.length })}
            </span>
          </span>
        ),
      },
      {
        id: "validity",
        header: t("pos.campaigns.fields.validity"),
        exportValue: (row) => validityText(row, t("pos.campaigns.openEnd")),
        cell: (row) => <span className="tabular">{validityText(row, t("pos.campaigns.openEnd"))}</span>,
      },
      {
        id: "window",
        header: t("pos.campaigns.fields.window"),
        exportValue: (row) =>
          `${weekdaysText(row.weekdays, t("pos.campaigns.everyDay"))} · ${timeWindowText(row.timeFrom, row.timeTo, t("pos.campaigns.allDay"))}`,
        cell: (row) => (
          <span className="grid gap-0.5 text-xs">
            <span>{weekdaysText(row.weekdays, t("pos.campaigns.everyDay"))}</span>
            <span>{timeWindowText(row.timeFrom, row.timeTo, t("pos.campaigns.allDay"))}</span>
          </span>
        ),
      },
      {
        id: "customerKind",
        header: t("pos.campaigns.fields.customerKind"),
        exportValue: (row) => customerKindText(row),
        cell: (row) => customerKindText(row),
      },
      {
        id: "stations",
        header: t("pos.campaigns.fields.stations"),
        exportValue: (row) =>
          row.stationIds.length === 0 ? t("pos.campaigns.allStations") : row.stationIds.length,
        cell: (row) =>
          row.stationIds.length === 0
            ? t("pos.campaigns.allStations")
            : t("pos.campaigns.stationCount", { count: row.stationIds.length }),
      },
      {
        id: "state",
        header: t("common.status"),
        exportValue: (row) => t(`pos.campaigns.state.${campaignState(row)}`),
        cell: (row) => {
          const state = campaignState(row);
          return (
            <StatusBadge tone={CAMPAIGN_STATE_TONE[state]}>{t(`pos.campaigns.state.${state}`)}</StatusBadge>
          );
        },
      },
      {
        id: "actions",
        header: t("common.actions"),
        exportValue: () => null,
        cell: (row) =>
          permissions.canManage ? (
            <span className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" onClick={() => onEdit({ campaign: row })}>
                {t("common.edit")}
              </Button>
              {row.isActive ? (
                <Button variant="outline" size="sm" onClick={() => setClosing(row)}>
                  {t("pos.campaigns.deactivate")}
                </Button>
              ) : null}
            </span>
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      },
    ],
    [t, permissions.canManage, onEdit],
  );

  if (station.error) return <Alert tone="danger">{errorMessage(station.error)}</Alert>;
  if (station.isLoading) return <SkeletonRows rows={4} />;
  if (station.stations.length === 0) return <NoStations />;

  return (
    <>
      <DataTable
        caption={t("pos.campaigns.caption")}
        columns={columns}
        rows={list.data?.items ?? []}
        rowKey={(row) => row.id}
        loading={list.isPending}
        error={list.error ? <Alert tone="danger">{errorMessage(list.error)}</Alert> : undefined}
        emptyState={
          <EmptyState
            headingLevel={2}
            icon={<Percent />}
            title={t("pos.campaigns.emptyTitle")}
            description={t("pos.campaigns.emptyDescription")}
          />
        }
        filters={
          <>
            <PosStationField selection={station} />
            <FormField label={t("common.status")} className="w-44">
              {(control) => (
                <Select
                  {...control}
                  value={search.active ?? ""}
                  onChange={(event) => setFilters({ active: event.target.value || undefined })}
                >
                  <option value="">{t("common.all")}</option>
                  <option value="true">{t("pos.campaigns.onlyActive")}</option>
                  <option value="false">{t("pos.campaigns.onlyInactive")}</option>
                </Select>
              )}
            </FormField>
          </>
        }
        pagination={{
          hasPrevious: pages.hasPrevious,
          hasNext: list.data?.page.hasMore ?? false,
          onPrevious: pages.previous,
          onNext: () => pages.next(list.data?.page.nextCursor),
          pageLabel: t("common.pageNumber", { page: pages.pageNumber }),
        }}
      />

      {permissions.canManage ? (
        <CampaignDialog
          open={editing !== null}
          onOpenChange={(next) => {
            if (!next) onEdit(null);
          }}
          companyId={companyId}
          campaign={editing?.campaign ?? null}
        />
      ) : null}
      <DeactivateCampaignDialog campaign={closing} onClose={() => setClosing(null)} />
    </>
  );
}

/** Kapatma onayı: kampanya SİLİNMEZ, yalnızca yürürlükten kalkar ve geçmiş fişlerde adı durur. */
function DeactivateCampaignDialog({
  campaign,
  onClose,
}: {
  campaign: CampaignRecord | null;
  onClose: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const refresh = useRefreshPos();
  const [error, setError] = useState<string | null>(null);

  const deactivate = useMutation({
    mutationFn: async () =>
      (
        await api.POST("/campaigns/{id}/deactivate", {
          params: { path: { id: campaign!.id } },
          body: { version: campaign!.version },
        })
      ).data!,
    onSuccess: async () => {
      await refresh();
      toast({ tone: "success", title: t("pos.campaigns.deactivated") });
      onClose();
    },
    onError: (caught) =>
      setError(isApiError(caught, "VERSION_CONFLICT") ? t("pos.campaigns.conflict") : errorMessage(caught)),
  });

  return (
    <Dialog
      open={campaign !== null}
      onOpenChange={(next) => {
        if (!next && !deactivate.isPending) {
          setError(null);
          onClose();
        }
      }}
      title={t("pos.campaigns.deactivateTitle")}
      description={t("pos.campaigns.deactivateDescription")}
      dismissible={!deactivate.isPending}
    >
      <div className="grid gap-4">
        {error ? <Alert tone="danger">{error}</Alert> : null}
        {campaign ? (
          <p className="text-sm">
            <span className="font-mono">{campaign.code}</span> · {campaign.name}
          </p>
        ) : null}
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button variant="outline" disabled={deactivate.isPending} onClick={onClose}>
            {t("common.cancel")}
          </Button>
          {/* Pencerenin kapatma (X) düğmesinin erişilebilir adı da "Kapat"tır: onay düğmesi AYRI ad taşır. */}
          <Button variant="destructive" loading={deactivate.isPending} onClick={() => deactivate.mutate()}>
            {t("pos.campaigns.deactivateConfirm")}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
